import { Box } from "@mui/material";
import type { FC, ReactNode } from "react";
import { InlineHint } from "../../UIToolkit/molecules/InlineHint";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";

type Props = {
	/** View-only access: the hint is shown on screen, not only in the Save tooltip. */
	isReadOnly: boolean;
	readOnlyHint: string;
	isDirty: boolean;
	canSave: boolean;
	isPending: boolean;
	/** Why Save is disabled, shown on hover. `undefined` when there is nothing to explain. */
	disabledReason: ReactNode | undefined;
	onDiscard: () => void;
	onSave: () => void;
	saveLabel: string;
	discardLabel: string;
	/** Prefix for the `-readonly-hint`, `-discard` and `-save` test ids. */
	dataTestId: string;
};

/**
 * The footer every Wage Settings section ends with: the read-only hint, then Discard and Save.
 * Each section owns its own draft and saves on its own, so each gets its own bar.
 */
export const WageSettingsSaveBar: FC<Props> = ({
	isReadOnly,
	readOnlyHint,
	isDirty,
	canSave,
	isPending,
	disabledReason,
	onDiscard,
	onSave,
	saveLabel,
	discardLabel,
	dataTestId,
}) => (
	<>
		<InlineHint tone="neutral" open={isReadOnly} dataTestId={`${dataTestId}-readonly-hint`} sx={{ width: "100%" }}>
			{readOnlyHint}
		</InlineHint>

		<Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, width: "100%", flexWrap: "wrap" }}>
			<SystemButton buttonVariant="borderless" onClick={onDiscard} disabled={!isDirty || isPending} noFullWidthMobile data-testid={`${dataTestId}-discard`}>
				{discardLabel}
			</SystemButton>
			<SystemButton
				buttonVariant="primary"
				onClick={onSave}
				disabled={!canSave}
				loading={isPending}
				tooltip={disabledReason}
				noFullWidthMobile
				data-testid={`${dataTestId}-save`}
			>
				{saveLabel}
			</SystemButton>
		</Box>
	</>
);
